/**
 * Detects if the Meex Chrome extension is installed using multiple methods
 * @returns {Promise<{installed: boolean, version: string|null}>}
 */
export const detectMeexExtension = () => {
  return new Promise((resolve) => {
    // Support both production and development extension IDs
    const extensionIds = [
      "dnjklpokjknbbjejbpcicakjefigcnda", // Chrome Web Store ID
      "opcmkbihpnkjpejfhccfhncklnanieag", // Local ID
    ];

    // Set timeout for fallback
    const timeoutId = setTimeout(() => {
      detectViaDOM().then((result) => {
        if (result.installed) {
          resolve(result);
        } else {
          resolve({ installed: false, version: null });
        }
      });
    }, 1000);

    // Only run in browser
    if (typeof window === "undefined") {
      clearTimeout(timeoutId);
      resolve({ installed: false, version: null });
      return;
    }

    // Check if on meex.co domain - extension should be active here
    const isMeexDomain = window.location.hostname.includes("meex.co");

    // Try DOM detection first on meex.co domains as it's more reliable there
    if (isMeexDomain) {
      detectViaDOM().then((result) => {
        if (result.installed) {
          clearTimeout(timeoutId);
          resolve(result);
          return;
        }

        // If DOM detection fails, fall through to messaging approach
        tryMessagingDetection();
      });
    } else {
      // On other domains, try messaging first
      tryMessagingDetection();
    }

    // Method 2: Detect via DOM elements or global variables injected by extension
    function detectViaDOM() {
      return new Promise((resolve) => {
        // Check for extension-specific elements or globals
        const hasMeexGlobal = typeof window.meexExtension !== "undefined";
        const hasMeexElement =
          document.getElementById("meex-extension-root") !== null;

        if (
          hasMeexGlobal &&
          window.meexExtension &&
          window.meexExtension.version
        ) {
          // Extension detected via global variable with version
          resolve({
            installed: true,
            version: window.meexExtension.version,
          });
        } else if (hasMeexElement) {
          // Extension detected via DOM element, but no version available
          const element = document.getElementById("meex-extension-root");
          const version = element.getAttribute("data-version") || null;
          resolve({
            installed: true,
            version: version,
          });
        } else {
          // No extension detected
          resolve({ installed: false, version: null });
        }
      });
    }

    // Method 1: Try detecting via Chrome messaging
    function tryMessagingDetection() {
      // Browser doesn't support chrome.runtime
      if (
        typeof chrome === "undefined" ||
        !chrome.runtime ||
        !chrome.runtime.sendMessage
      ) {
        // Always try DOM detection as fallback when chrome.runtime isn't available
        detectViaDOM().then((result) => {
          clearTimeout(timeoutId);
          resolve(result);
        });
        return;
      }

      let checkedCount = 0;
      let isResolved = false;

      // Try each extension ID
      extensionIds.forEach((extensionId) => {
        try {
          chrome.runtime.sendMessage(
            extensionId,
            { type: "DETECT_EXTENSION" },
            (response) => {
              // Check for lastError first to prevent console error
              if (chrome.runtime.lastError) {
                // Don't log the error, just count this ID as checked
                checkedCount++;

                // If we've tried all IDs and none worked, try DOM method
                if (checkedCount === extensionIds.length && !isResolved) {
                  detectViaDOM().then((result) => {
                    clearTimeout(timeoutId);
                    resolve(result);
                  });
                }
                return;
              }

              // Skip if we've already resolved with a successful detection
              if (isResolved) return;

              checkedCount++;

              // If we got a valid response, resolve with it
              if (response && response.installed) {
                isResolved = true;
                clearTimeout(timeoutId);
                resolve({ installed: true, version: response.version });
                return;
              }

              // If we've tried all IDs and none worked, try DOM method
              if (checkedCount === extensionIds.length && !isResolved) {
                detectViaDOM().then((result) => {
                  clearTimeout(timeoutId);
                  resolve(result);
                });
              }
            }
          );
        } catch (error) {
          // Count errors as checked IDs
          checkedCount++;

          // If all IDs have been tried, fall back to DOM method
          if (checkedCount === extensionIds.length && !isResolved) {
            detectViaDOM().then((result) => {
              clearTimeout(timeoutId);
              resolve(result);
            });
          }
        }
      });
    }
  });
};
